import { createContext, useState, useCallback, type ReactNode } from 'react';
import { TUTORIAL_STEPS } from './tutorialSteps';
import type { TutorialContextType, TutorialStep } from './types';
import { STORAGE_KEYS } from '@/config/appConfig';

export const TutorialContext = createContext<TutorialContextType | null>(null);

export const TutorialProvider = ({ children }: { children: ReactNode }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const currentStep: TutorialStep | null = isActive
    ? (TUTORIAL_STEPS[currentStepIndex] ?? null)
    : null;

  const startTutorial = useCallback(() => {
    setCurrentStepIndex(0);
    setIsActive(true);
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStepIndex((prev) => Math.min(prev + 1, TUTORIAL_STEPS.length - 1));
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStepIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const closeTutorial = useCallback((markSeen: boolean) => {
    setIsActive(false);
    if (markSeen) {
      localStorage.setItem(STORAGE_KEYS.tutorialSeen, 'true');
    }
  }, []);

  const skipTutorial = useCallback(() => closeTutorial(true), [closeTutorial]);
  const finishTutorial = useCallback(() => closeTutorial(true), [closeTutorial]);

  return (
    <TutorialContext.Provider
      value={{
        isActive,
        currentStepIndex,
        currentStep,
        startTutorial,
        nextStep,
        prevStep,
        skipTutorial,
        finishTutorial,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};
